/**
 * Sends an email with the provided contact information.
 * @param {Object} param0 - The contact form data.
 * @param {string} param0.email - The sender's email address.
 * @param {string} param0.name - The sender's full name.
 * @param {string} param0.message - The message content.
 * @returns {Promise<{ status: 'success' } | { status: 'error', fields: { [key: string]: string } }>} A promise that resolves to a success object or an error object with field-specific messages.
 */
export const sendEmailAsync = async ({ email, name, message }) => {
  // Collect error messages as a map
  const errors = {};

  // Type checking functions
  const isString = (value) => typeof value === 'string';

  // Validation functions
  const isValidName = (name) => isString(name) && /^[a-zA-Z-' ]+$/.test(name);
  const isValidEmailFormat = (email) => isString(email) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidMessage = (message) => isString(message) && message.trim().length > 0;

  // Type and presence validation
  if (!isString(email)) {
    errors.email = email === undefined ? 'Email is required' : 'Enter a valid email';
  }

  if (!isString(name)) {
    errors.name = name === undefined ? 'Name is required' : 'Enter a name';
  }

  if (!isString(message)) {
    errors.message = message === undefined ? 'Message is required' : 'Enter a message';
  }

  // Format validation (only if type is valid)
  if (isString(name) && !isValidName(name)) {
    errors.name = 'Enter a valid name';
  }

  if (isString(email) && !isValidEmailFormat(email)) {
    errors.email = 'Enter a valid email address';
  }

  if (isString(message) && !isValidMessage(message)) {
    errors.message = 'Enter a message';
  }

  // If there are any errors, return them
  if (Object.keys(errors).length > 0) {
    return { status: 'error', fields: errors };
  }

  const currentPageUrl = window.location.href; // Get current page URL

  try {
    const response = await fetch('/api/contact-us', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name, email, message, currentPageUrl }),
    });

    if (!response.ok) {
      return { status: 'error', fields: { network: 'Failed to send form data' } };
    }

    await response.json(); // Parse response
    return { status: 'success' };
  } catch (error) {
    return { status: 'error', fields: { network: 'Failed to send form data' } };
  }
};
